import { oc } from "@orpc/contract";
import { z } from "zod";
import { BackgroundJobStatus } from "@erp/shared-types";
import {
  PresignedUploadUrlInputSchema,
  PresignedUploadUrlResponseSchema,
} from "../shared/presignedUpload";
import {
  FeedbackSupplierSchema,
  type FeedbackSupplier,
} from "./hubbell-feedback.contract";
import { RENEWAL_RULE_TYPES } from "./renewal-rules.contract";

export { productHoldDivision, productHoldItems } from "./product-hold-list";
export { exceptionFileScope, exceptionDivision } from "./exception-file-scope";

/**
 * The Mass Renewal upload (docs/pricing/mass-renewal-upload.md), as a
 * background run.
 *
 * One run takes a supplier's Results Workbook, one to nine filled-in Feedback
 * Files and an optional Exception List, all staged in blob storage by the
 * browser through presigned URLs, and applies the organization's Renewal
 * Rules (snapshotted when the run starts) to produce, per Division, the AS400
 * Header Cross upload CSV, the exceptions CSV and the Renewal Report. `start`
 * answers with the job, `getLatestRun` is polled for progress and the run
 * report, and `getDownloadUrl` signs a short-lived link per Division and file
 * once the run has succeeded. At most one run is active per organization and
 * supplier; a new start replaces the previous run's files.
 *
 * The run report schemas live here rather than in the backend because the
 * report is recorded on the job's payload and read back by the page: both
 * sides validate the same shape.
 */

/**
 * Why a line was left out of both uploads before it was priced (as in the
 * Hubbell layout, AIA-29). A line the Exception List prices is never skipped
 * for a reason after the first: its SELL NET needs none of those columns.
 */
export const MASS_RENEWAL_LINE_SKIP_REASONS = [
  "no AS400 Item",
  "blank Pricing Factor",
  "blank FINAL PRIOR MARGINS",
  "blank quote cost",
  "zero quote cost",
] as const;
export type MassRenewalLineSkipReason =
  (typeof MASS_RENEWAL_LINE_SKIP_REASONS)[number];

/** Why a Division was not built. Client-neutral wording; the UI translates. */
export const MASS_RENEWAL_SKIP_REASONS = [
  "no feedback file",
  "two feedback files name this Division",
  "tab not in the Results Workbook",
  "tab not in the Division table",
  "feedback file does not match the results",
] as const;
export type MassRenewalSkipReason = (typeof MASS_RENEWAL_SKIP_REASONS)[number];

const count = z.number().int().nonnegative();

/**
 * Why a Division's "Hold when cost unchanged" rule could not check some of
 * its lines: the Results Workbook has no PRIOR CORE COST NET column, or the
 * column is blank on those lines. Such a line is never held by that rule.
 */
export const MASS_RENEWAL_DIVISION_WARNINGS = [
  "no PRIOR CORE COST NET column",
  "blank PRIOR CORE COST NET",
] as const;
export type MassRenewalDivisionWarningCode =
  (typeof MASS_RENEWAL_DIVISION_WARNINGS)[number];

export const MassRenewalDivisionWarningSchema = z.object({
  warning: z.enum(MASS_RENEWAL_DIVISION_WARNINGS),
  /** Uploaded lines the warning covers. */
  lines: count,
});
export type MassRenewalDivisionWarning = z.infer<
  typeof MassRenewalDivisionWarningSchema
>;

/** Why an Exception List row priced no line. Client-neutral wording; the UI translates. */
export const MASS_RENEWAL_UNMATCHED_EXCEPTION_REASONS = [
  "no line with this item",
  "no line matched its filters",
  "matched only lines that failed",
  "matched only lines of a skipped Division",
] as const;
export type MassRenewalUnmatchedExceptionReason =
  (typeof MASS_RENEWAL_UNMATCHED_EXCEPTION_REASONS)[number];

/** The lines one rule held (both increases set to 0). */
export const MassRenewalHeldByRuleSchema = z.object({
  ruleId: z.string(),
  name: z.string(),
  type: z.enum(RENEWAL_RULE_TYPES),
  count,
});
export type MassRenewalHeldByRule = z.infer<typeof MassRenewalHeldByRuleSchema>;

export const MassRenewalBuiltDivisionSchema = z.object({
  status: z.literal("built"),
  /** The Division's tab letter. */
  letter: z.string(),
  /** The two-character Division code (`WT`); names the Division's files. */
  code: z.string(),
  name: z.string(),
  feedbackFileName: z.string(),
  counts: z.object({
    /** Non-blank rows read. */
    lines: count,
    uploaded: count,
    exceptions: count,
    /** Lines left out of both uploads for a problem of their own; the Renewal Report's Failed Lines sheet lists them. */
    errors: count,
    /** Uploaded lines a margin floor raised. */
    floored: count,
    /** Uploaded lines whose Load has no Feedback row (no increase). */
    noFeedbackRow: count,
    skipped: z.record(z.enum(MASS_RENEWAL_LINE_SKIP_REASONS), count),
  }),
  held: z.array(MassRenewalHeldByRuleSchema),
  /** The first line errors, by sheet row; the Renewal Report has them all. */
  errors: z.array(
    z.object({ sheetRow: z.number().int(), message: z.string() }),
  ),
  /** Rules that could not do their job on some lines. */
  warnings: z.array(MassRenewalDivisionWarningSchema).default([]),
});
export type MassRenewalBuiltDivision = z.infer<
  typeof MassRenewalBuiltDivisionSchema
>;

export const MassRenewalSkippedDivisionSchema = z.object({
  status: z.literal("skipped"),
  letter: z.string(),
  /** Null for a tab the Division table does not know. */
  code: z.string().nullable(),
  reason: z.enum(MASS_RENEWAL_SKIP_REASONS),
  /**
   * "feedback file does not match the results": the share (0–1) of the
   * Feedback File's rows with a Quote SPA whose Load has that QUOTE SPA in
   * the Division's results. Below 0.75 the Division is skipped.
   */
  share: z.number().min(0).max(1).optional(),
});
export type MassRenewalSkippedDivision = z.infer<
  typeof MassRenewalSkippedDivisionSchema
>;

export const MassRenewalDivisionOutcomeSchema = z.discriminatedUnion("status", [
  MassRenewalBuiltDivisionSchema,
  MassRenewalSkippedDivisionSchema,
]);
export type MassRenewalDivisionOutcome = z.infer<
  typeof MassRenewalDivisionOutcomeSchema
>;

export const MassRenewalRunReportSchema = z.object({
  supplier: FeedbackSupplierSchema,
  /** In Division table order, then any tab the table does not know. */
  divisions: z.array(MassRenewalDivisionOutcomeSchema),
  /** Exception List rows that priced no line, with why. */
  unmatchedExceptionRows: z.array(
    z.object({
      sheetRow: z.number().int(),
      item: z.string(),
      sellNet: z.string(),
      margin: z.number().optional(),
      sourceFile: z.string().optional(),
      reason: z.enum(MASS_RENEWAL_UNMATCHED_EXCEPTION_REASONS),
      /** For "matched only lines that failed": the first such line's problem. */
      failure: z.string().nullable(),
    }),
  ),
  /** Feedback Files with no "<code> Feedback" sheet of a known Division. */
  unknownFeedbackFiles: z.array(z.string()),
});
export type MassRenewalRunReport = z.infer<typeof MassRenewalRunReportSchema>;

/** The three files a built Division has. */
export const MASS_RENEWAL_DOWNLOAD_FILES = [
  "upload",
  "exceptions",
  "report",
] as const;
export const MassRenewalDownloadFileSchema = z.enum(
  MASS_RENEWAL_DOWNLOAD_FILES,
);
export type MassRenewalDownloadFile = z.infer<
  typeof MassRenewalDownloadFileSchema
>;

const SUPPLIER_FILE_CODES: Readonly<Record<FeedbackSupplier, string>> = {
  hubbell: "HUB",
  abb: "ABB",
};

const DOWNLOAD_SUFFIXES: Readonly<Record<MassRenewalDownloadFile, string>> = {
  upload: "MASS RENEWAL UPLOAD.csv",
  exceptions: "MASS RENEWAL EXCEPTIONS.csv",
  report: "MASS RENEWAL REPORT.xlsx",
};

/** `WT - HUB - MASS RENEWAL UPLOAD.csv`: the name a file downloads under. */
export function massRenewalDownloadName(
  code: string,
  supplier: FeedbackSupplier,
  file: MassRenewalDownloadFile,
): string {
  return `${code} - ${SUPPLIER_FILE_CODES[supplier]} - ${DOWNLOAD_SUFFIXES[file]}`;
}

/** A staged upload the run reads: the key `getUploadUrl` minted, and the file's own name. */
export const MassRenewalStagedFileSchema = z.object({
  uploadKey: z.string().min(1),
  fileName: z.string().min(1).max(255),
});
export type MassRenewalStagedFile = z.infer<typeof MassRenewalStagedFileSchema>;

/** The Division table has nine tabs; a drop never has more Feedback Files than that. */
export const MASS_RENEWAL_MAX_FEEDBACK_FILES = 9;
export const MASS_RENEWAL_MAX_EXCEPTION_FILES = 20;

export const StartMassRenewalRunInputSchema = z.object({
  /** Whose Results Workbook it is. */
  supplier: FeedbackSupplierSchema,
  resultsWorkbook: MassRenewalStagedFileSchema,
  /** The filled-in Feedback Files: each builds the Division its "<code> Feedback" sheet names. */
  feedbackFiles: z
    .array(MassRenewalStagedFileSchema)
    .min(1)
    .max(MASS_RENEWAL_MAX_FEEDBACK_FILES),
  /** Fixed SELL NET overrides, or a Division-scoped product hold list for this run. */
  exceptionList: MassRenewalStagedFileSchema.optional(),
  exceptionLists: z
    .array(MassRenewalStagedFileSchema)
    .max(MASS_RENEWAL_MAX_EXCEPTION_FILES)
    .optional(),
});
export type StartMassRenewalRunInput = z.infer<
  typeof StartMassRenewalRunInputSchema
>;

/** The inputs a run was started with, as the page shows them (names only). */
export const MassRenewalRunInputsSchema = z.object({
  resultsWorkbook: z.object({ fileName: z.string() }),
  feedbackFiles: z.array(z.object({ fileName: z.string() })),
  exceptionList: z.object({ fileName: z.string() }).nullable(),
  exceptionLists: z.array(z.object({ fileName: z.string() })).optional(),
});

/**
 * One run, from the organization's point of view: the job's state plus what
 * the run has reported so far. `progress` is every Division built at the time
 * of the read (the run reports each one as its lines are all renewed);
 * `report` is set once the run has succeeded and is the complete picture.
 */
export const MassRenewalRunSchema = z.object({
  backgroundJobId: z.uuid(),
  supplier: FeedbackSupplierSchema,
  status: z.enum(BackgroundJobStatus),
  /** 0-100 while running, from Divisions built over Feedback Files given; null before the first. */
  percent: z.number().nullable(),
  /** Safe to show verbatim; present only on failure. */
  errorMessage: z.string().nullable(),
  /**
   * Whether the caller started this run. The run is the organization's to
   * watch, but its downloads are the starter's (the job lookup behind them is
   * user-scoped), so the page offers them only when this is true.
   */
  startedByCurrentUser: z.boolean(),
  /** The starter's name, to say whose downloads they are; null when unknown. */
  startedByName: z.string().nullable(),
  /** How many enabled Renewal Rules for this supplier the run was started with. */
  ruleCount: z.number().int().nonnegative(),
  /**
   * When the files stop being downloadable (the export retention, counted
   * from the run's completion); null until the run has succeeded.
   */
  downloadsExpireAt: z.date().nullable(),
  createdAt: z.date(),
  startedAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  inputs: MassRenewalRunInputsSchema,
  progress: z.array(MassRenewalBuiltDivisionSchema),
  report: MassRenewalRunReportSchema.nullable(),
});
export type MassRenewalRun = z.infer<typeof MassRenewalRunSchema>;

export const GetMassRenewalDownloadUrlInputSchema = z.object({
  backgroundJobId: z.uuid(),
  /** The Division's tab letter, as the report names it. */
  letter: z.string().min(1).max(4),
  file: MassRenewalDownloadFileSchema,
});

export const GetMassRenewalDownloadUrlResponseSchema = z.object({
  /** Signed, short-lived (minutes), and downloads under `fileName`. */
  url: z.string(),
  fileName: z.string(),
});

export const massRenewalContracts = {
  /** Mint a presigned URL for the browser to PUT one input workbook to. */
  getUploadUrl: oc
    .input(PresignedUploadUrlInputSchema)
    .output(PresignedUploadUrlResponseSchema)
    .route({
      summary: "Get an upload URL for a Mass Renewal input workbook",
      description:
        "Returns a presigned URL for uploading one Mass Renewal input (the Results Workbook, a filled-in Feedback File or the Exception List; .xlsx only, up to 100 MB). Pass the returned uploadKey to start.",
    }),

  /**
   * Start a run on the staged inputs. Answers with the active run for the
   * organization and supplier when one is already going rather than starting
   * a second one.
   */
  start: oc
    .input(StartMassRenewalRunInputSchema)
    .output(MassRenewalRunSchema)
    .route({
      method: "POST",
      summary: "Start a Mass Renewal run",
      description:
        "Starts a background run that prices a Hubbell or ABB Results Workbook from the staged Feedback Files, the organization's enabled Renewal Rules (snapshotted now) and an optional Exception List, writing per Division the AS400 upload CSV, the exceptions CSV and the Renewal Report. Replaces the previous run's files for that supplier; returns the active run when one is already in progress.",
    }),

  /** Separate endpoint: older servers must reject rather than strip exceptionLists. */
  startWithExceptionLists: oc
    .input(StartMassRenewalRunInputSchema)
    .output(MassRenewalRunSchema)
    .route({
      method: "POST",
      summary: "Start a Mass Renewal run with multiple exception lists",
      description:
        "Starts a run supporting supplier-item target margins, core-cost adders and multiple exception files. Requires a compatible API and worker; older servers reject this procedure instead of silently omitting exceptions.",
    }),

  /** The organization's most recent run for one supplier, or null before the first. */
  getLatestRun: oc
    .input(z.object({ supplier: FeedbackSupplierSchema }))
    .output(z.object({ run: MassRenewalRunSchema.nullable() }))
    .route({
      method: "GET",
      summary: "Get the latest Mass Renewal run",
      description:
        "Returns the organization's most recent Mass Renewal run for one supplier (hubbell or abb) with its status, progress, the Divisions built so far and the run report (per-Division counts, lines held by each rule, skipped Divisions, unmatched Exception List rows), or null before any run.",
    }),

  /** A short-lived link to one built Division's upload, exceptions or report file. */
  getDownloadUrl: oc
    .input(GetMassRenewalDownloadUrlInputSchema)
    .output(GetMassRenewalDownloadUrlResponseSchema)
    .route({
      method: "GET",
      summary: "Get a download link for a Mass Renewal file",
      description:
        'Returns a temporary download link for one built Division of a succeeded Mass Renewal run the signed-in user started: file "upload" (the AS400 upload CSV), "exceptions" (the Exception List lines CSV) or "report" (the Renewal Report xlsx). Files are kept for 30 days.',
    }),
} as const;
